// src/components/Branch/config/sections/TestimonialsSection.jsx
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChatAlt2Icon, PlusIcon, PencilIcon, TrashIcon, CheckCircleIcon, StarIcon } from "@heroicons/react/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/solid";
import { addTestimonialThunk, updateTestimonialThunk, deleteTestimonialThunk } from "../../../../store/thunks/branchDisplayThunks";

const INITIAL_FORM = { customerName: "", customerLocation: "", rating: 5, review: "", occasion: "", isVisible: true };

const TestimonialsSection = ({ branchCode, isRootAdmin }) => {
  const dispatch = useDispatch();
  const { testimonials, loadingSection, uploading } = useSelector((state) => state.branchDisplay);
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleAdd = async () => {
    const form = new FormData();
    Object.entries(formData).forEach(([k, v]) => form.append(k, v));
    await dispatch(addTestimonialThunk({ branchCode, formData: form }));
    handleCancel();
  };

  const handleEdit = (testimonial, index) => {
    setEditingIndex(index);
    setFormData({
      customerName: testimonial.customerName || "",
      customerLocation: testimonial.customerLocation || "",
      rating: testimonial.rating || 5,
      review: testimonial.review || "",
      occasion: testimonial.occasion || "",
      isVisible: testimonial.isVisible ?? true,
    });
  };

  const handleUpdate = async () => {
    await dispatch(updateTestimonialThunk({ branchCode, testimonialIndex: editingIndex, data: formData }));
    handleCancel();
  };

  const handleDelete = async (index) => {
    if (window.confirm("Delete this testimonial?")) {
      await dispatch(deleteTestimonialThunk({ branchCode, testimonialIndex: index }));
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingIndex(null);
    setFormData(INITIAL_FORM);
  };

  const renderForm = () => (
    <div className="bg-white border rounded-lg p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Customer Name *</label>
          <input type="text" value={formData.customerName} onChange={e => handleInputChange("customerName", e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="Customer name" />
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Location</label>
          <input type="text" value={formData.customerLocation} onChange={e => handleInputChange("customerLocation", e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="City, State" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-600 mb-1">Rating</label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(star => (
              <button key={star} type="button" onClick={() => handleInputChange("rating", star)} className="text-yellow-400">
                {star <= formData.rating ? <StarSolidIcon className="h-6 w-6" /> : <StarIcon className="h-6 w-6" />}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm text-gray-600 mb-1">Occasion</label>
          <input type="text" value={formData.occasion} onChange={e => handleInputChange("occasion", e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g., Wedding, Anniversary" />
        </div>
      </div>
      <div>
        <label className="block text-sm text-gray-600 mb-1">Review *</label>
        <textarea value={formData.review} onChange={e => handleInputChange("review", e.target.value)} className="w-full px-3 py-2 border rounded-lg" rows="3" placeholder="Customer review..." />
      </div>
      <div className="flex gap-2">
        <button onClick={editingIndex !== null ? handleUpdate : handleAdd} disabled={(loadingSection === "testimonials" || uploading) || !formData.customerName || !formData.review} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 flex items-center gap-2">
          <CheckCircleIcon className="h-4 w-4" /> {editingIndex !== null ? "Update" : "Add"} Testimonial
        </button>
        <button onClick={handleCancel} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg">Cancel</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {isRootAdmin && !isAdding && editingIndex === null && (
        <button onClick={() => setIsAdding(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
          <PlusIcon className="h-4 w-4" /> Add Testimonial
        </button>
      )}
      {(isAdding || editingIndex !== null) && renderForm()}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium mb-4 flex items-center"><ChatAlt2Icon className="h-4 w-4 mr-2" /> Testimonials ({testimonials.length})</h4>
        {testimonials.length === 0 ? <p className="text-center py-8 text-gray-500">No testimonials added</p> : (
          <div className="space-y-4">
            {testimonials.map((testimonial, index) => (
              <div key={index} className={`bg-white border rounded-lg p-4 ${!testimonial.isVisible ? "opacity-60" : ""}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium">{testimonial.customerName}</span>
                      {testimonial.customerLocation && <span className="text-sm text-gray-500">• {testimonial.customerLocation}</span>}
                    </div>
                    <div className="flex gap-0.5 mb-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <StarSolidIcon key={star} className={`h-4 w-4 ${star <= testimonial.rating ? "text-yellow-400" : "text-gray-300"}`} />
                      ))}
                    </div>
                    <p className="text-sm text-gray-600">{testimonial.review}</p>
                    {testimonial.occasion && <p className="text-xs text-blue-600 mt-2">{testimonial.occasion}</p>}
                  </div>
                  {isRootAdmin && editingIndex === null && (
                    <div className="flex gap-1 ml-2">
                      <button onClick={() => handleEdit(testimonial, index)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"><PencilIcon className="h-4 w-4" /></button>
                      <button onClick={() => handleDelete(index)} className="p-1 text-red-600 hover:bg-red-50 rounded"><TrashIcon className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TestimonialsSection;
